import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, viewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { environment } from '@work-frontend-monorepo/work-environments';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { AsyncPipe } from '@angular/common';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { AppDeviceService } from './global/services/app-device.service';
import { AppLanguageService } from './global/services/app-language.service';
import { PushNotificationsService } from './global/services/push-notifications.service';
import { Subscription } from 'rxjs';
import { AlertDialogsDirective, AlertDialogService, ConfirmActionModalHostDirective, ConfirmActionModalService, ModalDirective, ModalService } from '@work-frontend-monorepo/work-ui/modals';
import { EnvironmentRibbonComponent, LoadingSpinnerComponent } from '@work-frontend-monorepo/work-ui/layout';
import { LoadingState, ProfileState, ProfileStateSetProfile } from '@work-frontend-monorepo/work-ui/state-management';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  imports: [
    AlertDialogsDirective,
    AsyncPipe,
    ConfirmActionModalHostDirective,
    EnvironmentRibbonComponent,
    IonApp,
    IonRouterOutlet,
    LoadingSpinnerComponent,
    ModalDirective,
    RouterModule,
  ],
  selector: 'sfg-app-root',
  templateUrl: './app.html',
})
export class App implements OnInit, AfterViewInit {
  // Injected services
  protected readonly environment = environment;
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly confirmActionModalService = inject(ConfirmActionModalService);
  private readonly modalService = inject(ModalService);
  private readonly store = inject(Store);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly actions = inject(Actions);
  private readonly appDeviceService = inject(AppDeviceService);
  private readonly pushNotificationsService = inject(PushNotificationsService);
  private readonly appLanguageService = inject(AppLanguageService);
  private readonly destroyRef = inject(DestroyRef);

  // ViewChild signals
  readonly alertDialogsHost = viewChild.required<AlertDialogsDirective>(AlertDialogsDirective);
  readonly confirmActionModalHost = viewChild.required<ConfirmActionModalHostDirective>(ConfirmActionModalHostDirective);
  readonly modalHost = viewChild.required<ModalDirective>(ModalDirective);

  // State selectors
  protected isLoggedIn$ = this.store.select(ProfileState.profile);
  protected showLoading$ = this.store.select(LoadingState.showLoadingState);

  constructor() {
    try {
      Sentry.setUser({
        id: this.store.selectSnapshot(ProfileState.profile)?.id ?? undefined,
        email: this.store.selectSnapshot(ProfileState.user)?.email ?? undefined,
      });
    } catch (e) {
      // Nothing to do.
    }
    this.appLanguageService.initialize().catch();

    // Initialize push notifications on app start (permissions + listeners)
    // but don't save token yet
    this.pushNotificationsService.initialize().catch(() => {
      // Permission denied or error - that's ok
    });
  }

  ngOnInit() {
    this.setTenantBasedFavicon();
    this.listenForLoginEvent().catch();
  }

  ngAfterViewInit() {
    this.showLoading$.subscribe({
      next: () => {
        this.changeDetectorRef.detectChanges();
      },
    });

    // Sicherstellen, dass die ViewChild-Referenzen existieren, bevor wir sie setzen
    this.alertDialogService.setAlertDialogsHost(this.alertDialogsHost());
    this.confirmActionModalService.setModalHost(this.confirmActionModalHost());
    this.modalService.setModalHost(this.modalHost());
  }

  /**
   * Listen for login event to register push notification
   * tokens (incl. permission request etc.)
   *
   * @private
   */
  private async listenForLoginEvent() {
    this.actions.pipe(ofActionCompleted(ProfileStateSetProfile), takeUntilDestroyed(this.destroyRef)).subscribe(async () => {
      // Initial registration of device.
      await this.appDeviceService.initializeAppDeviceInformation();

      const profile = this.store.selectSnapshot(ProfileState.profile);
      if (profile?.isUserEntity) {
        // Enable token saving and force re-registration if needed
        await this.pushNotificationsService.enableTokenSaving();
        await this.pushNotificationsService.initialize(true);
      } else {
        // Enable token saving without forcing
        await this.pushNotificationsService.enableTokenSaving();
      }
    });
  }

  /**
   * Set favicon for tenant.
   *
   * @private
   */
  private setTenantBasedFavicon() {
    const faviconPath = environment.tenantFaviconPath;
    if (faviconPath) {
      const link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
      if (link) {
        link.href = faviconPath;
      }
    }
  }
}
